import type {
  CoachWithRelations,
  PrescriptionWithRelations,
  UsersWithRelations,
} from "@ch/generated";
import moment from "moment";
import { getSourceName } from "../helpers/utils/helpers";

export const preparePrescriptionTemplate = (
  prescriptionData: PrescriptionWithRelations,
  member: UsersWithRelations & {
    facilityName: string;
    facilityLocation: string;
  },
  coach?: CoachWithRelations,
) => {
  const { medicationPassports, title } = prescriptionData;
  let tableBody = "";
  medicationPassports?.forEach((medicationPassport) => {
    const row = `<tr class="medications-table__row">
      <td>${medicationPassport?.medicine?.name}</td>
      <td>${moment(medicationPassport?.startDate).format("Do MMM, YYYY")}</td>
      <td>${medicationPassport?.durationInDays}</td>
      <td>${getSourceName(medicationPassport?.source)}</td>
      <td>${medicationPassport?.frequency}</td>
      <td>${medicationPassport?.dosage} ${medicationPassport?.dosagesUnit}</td>
    </tr>`;
    tableBody += row;
  });

  const template = `
<!DOCTYPE html>
<html
  xmlns="http://www.w3.org/1999/xhtml"
  xmlns:v="urn:schemas-microsoft-com:vml"
  xmlns:o="urn:schemas-microsoft-com:office:office"
>
<head>
  <title> </title>

  <meta http-equiv="X-UA-Compatible" content="IE=edge" />

  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style type="text/css">
      #outlook a {
          padding: 0;
      }
      body {
          margin: 0;
          padding: 0;
          -webkit-text-size-adjust: 100%;
          -ms-text-size-adjust: 100%;
      }
      table,
      td {
          border-collapse: collapse;
          mso-table-lspace: 0pt;
          mso-table-rspace: 0pt;
          border: 0;
      }
      img {
          border: 0;
          height: auto;
          line-height: 100%;
          outline: none;
          text-decoration: none;
          -ms-interpolation-mode: bicubic;
      }
      p {
          display: block;
          margin: 13px 0;
      }
      
      #medications-table thead th {
        padding: 0 5px;
      }
      
      .medications-table__row td {
         padding: 0 5px 10px;
      }
  </style>

  <link
    href="https://fonts.googleapis.com/css?family=Ubuntu:300,400,500,700"
    rel="stylesheet"
    type="text/css"
  />
  <style type="text/css">
      @import url(https://fonts.googleapis.com/css?family=Ubuntu:300,400,500,700);
  </style>
  <!--<![endif]-->
  <style type="text/css">
      .wrapper {
          width: 700px;
          margin: 0 auto;
      }
      @media only screen and (max-width: 800px) {
          .wrapper {
              width: 100%;
          }
      }
      @media only screen and (min-width: 480px) {
          .mj-column-per-50 {
              width: 50% !important;
              max-width: 50%;
          }
          .mj-column-per-100 {
              width: 100% !important;
              max-width: 100%;
          }
          .mj-column-per-33-333333333333336 {
              width: 33.333333333333336% !important;
              max-width: 33.333333333333336%;
          }
      }
  </style>
  <style media="screen and (min-width:480px)">
      .moz-text-html .mj-column-per-50 {
          width: 50% !important;
          max-width: 50%;
      }
      .moz-text-html .mj-column-per-100 {
          width: 100% !important;
          max-width: 100%;
      }
      .moz-text-html .mj-column-per-33-333333333333336 {
          width: 33.333333333333336% !important;
          max-width: 33.333333333333336%;
      }
  </style>
  <style type="text/css">
      @media only screen and (max-width: 480px) {
          table.mj-full-width-mobile {
              width: 100% !important;
          }
          td.mj-full-width-mobile {
              width: auto !important;
          }
      }
  </style>
</head>
<body style="word-spacing: normal;">
<div class="wrapper" style="border-top: 10px solid #47b462; background: #ffffff; box-shadow: 2px 2px 12px #eee;">
  <div class="divider" style="border-bottom: 2px solid #ff632f; margin: 0 auto; height: 2px; width: 100%; max-width: 100%;">
    <table  cellpadding="0" cellspacing="0" role="presentation" style="width: 100%">
      <tr>
        <td style="background: #ffffff; padding: 0px;">
          <div class="mj-hero-content" style="margin: 0px auto;">
            <table  cellpadding="0" cellspacing="0" role="presentation" style="width: 100%; margin: 0px">
              <tr>
                <td style="padding: 0px;"></td>
              </tr>
            </table>
          </div>
        </td>
      </tr>
    </table>
  </div>
  <div class="header" style="height: 120px; margin: 0px auto; max-width: 80%;">
    <table  cellpadding="0" cellspacing="0" role="presentation" style="width: 100%; text-align: center;">
      <tbody>
      <tr>
        <td style="font-size: 0px; padding: 20px 0; text-align: center;">
          <div class="mj-column-per-50 mj-outlook-group-fix imagetype" style="display: inline-block; vertical-align: top; width: 100%;">
            <table  cellpadding="0" cellspacing="0" role="presentation" style="width: 100%;">
              <tbody>
              <tr>
                <td align="center" style="font-size: 0px; padding: 10px 25px; word-break: break-word;">
                  <table  cellpadding="0" cellspacing="0" role="presentation" style="border-collapse: collapse;">
                    <tbody>
                    <tr>
                      <td style="width: 150px;">
                        <svg color="currentColor" fill="none" height="42" viewBox="0 0 82 30" width="114" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#LogoIconA)"><g fill="#414042"><path d="M50.434 13.566l-.012-5.849c-.007-.466.136-.577.578-.562l5.503.093c2.814.29 4.888 2.327 5.279 5.135.211 1.514.068 2.982-.625 4.351-1.127 2.228-3.059 3.17-5.466 3.238-1.586.045-3.175-.006-4.762.018-.415.007-.507-.129-.503-.519l.008-5.906h0zm2.414-.065l-.01 3.612c-.009.41.121.521.516.503l1.95-.008c2.49 0 3.862-1.171 4.124-3.644.212-2.006-.857-4.211-3.424-4.459-.897-.086-1.796-.003-2.693-.051-.395-.021-.477.131-.471.493l.007 3.555zm-16.453-1.615c1.22-1.464 3.937-1.535 5.037.498.912-1.332 2.195-1.71 3.717-1.482 1.657.248 2.699 1.278 2.965 2.952a8.27 8.27 0 0 1 .108 1.369l.014 4.071c.025.615-.182.765-.735.697a4.44 4.44 0 0 0-.974-.001c-.437.044-.593-.084-.582-.557l.012-4.358c0-.287-.015-.569-.097-.85-.182-.632-.518-1.107-1.203-1.235a1.75 1.75 0 0 0-1.791.643c-.377.464-.508 1.014-.507 1.606l.01 4.243c.007.404-.109.54-.513.509-.437-.034-.881-.031-1.319 0-.366.025-.45-.115-.446-.458l.007-4.358c-.003-1.371-.726-2.283-1.806-2.285-1.089-.002-1.887.944-1.901 2.294l.006 4.301c.007.406-.113.538-.514.507-.437-.033-.881-.031-1.319 0-.368.026-.446-.119-.444-.46l-.003-8.143c-.003-.401.138-.49.493-.459a4.46 4.46 0 0 0 .917-.001c.533-.066.85.071.791.679-.007.068.036.141.076.281l.001-.001zm35.974 3.629c-.011 2.766-1.953 4.685-4.718 4.662s-4.645-1.951-4.624-4.746c.021-2.752 1.93-4.643 4.675-4.632 2.757.011 4.677 1.952 4.666 4.717v-.001zm-2.265-.01c.012-1.493-1.035-2.656-2.407-2.673-1.389-.017-2.434 1.101-2.448 2.619s.999 2.636 2.408 2.649c1.436.014 2.434-1.045 2.446-2.595h.001zm3.255-.027c.027-1.898.665-3.474 2.457-4.275 1.779-.795 3.56-.778 5.196.477.333.256.426.458.044.725-.031.021-.053.055-.079.083-.288.306-.527.695-.875.897-.406.236-.67-.283-1.033-.402-1.16-.381-2.401-.014-3.04.969-.68 1.045-.538 2.626.309 3.445.822.795 2.364.935 3.35.237.364-.258.554-.232.821.081a6.32 6.32 0 0 0 .826.793c.306.248.215.398-.046.606-1.686 1.348-3.547 1.459-5.444.63-1.826-.798-2.464-2.39-2.488-4.266z"></path></g><g fill="#4abe82"><path d="M28.503 17.172a33.66 33.66 0 0 1 1.406 5.536c.373 2.251.555 4.512.307 6.794-.034.314-.092.471-.463.477-6.416.092-12.459-1.184-17.849-4.85-1.702-1.157-3.219-2.521-4.501-4.14a.84.84 0 0 1-.141-.939c.384-.972.966-1.817 1.599-2.637.204-.264.323-.269.549-.014 1.336 1.505 2.785 2.891 4.407 4.09 2.887 2.136 6.054 3.629 9.627 4.191a16.89 16.89 0 0 0 2.399.187c.399.006.486-.165.498-.5.043-1.2-.208-2.36-.523-3.5-.14-.508-.078-.809.339-1.168.759-.653 1.253-1.534 1.744-2.401.187-.331.359-.67.602-1.125h0zM2.299 12.805c-.491-1.364-.829-2.669-1.108-3.988C.63 6.171.367 3.499.566.789.607.251.723.002 1.352 0c6.212-.018 12.07 1.237 17.315 4.733a21.06 21.06 0 0 1 4.662 4.216c.302.369.371.695.178 1.141-.383.886-.917 1.669-1.491 2.437-.26.347-.4.379-.705.029-1.809-2.072-3.86-3.87-6.216-5.302-3.055-1.857-6.32-3.045-9.942-3.12-.537-.011-.706.165-.696.676.023 1.215.282 2.387.592 3.55.087.329.063.536-.222.777-.981.831-1.596 1.939-2.194 3.053l-.334.613v.001z"></path></g><g fill="#9e9f9e"><path d="M13.268 27.969c-2.306.828-4.604 1.425-6.97 1.76-1.668.237-3.341.325-5.026.24C.79 29.945.6 29.852.583 29.3c-.128-4.197.318-8.308 1.748-12.278 1.334-3.705 3.346-6.971 6.273-9.654.599-.549 1.124-.717 1.863-.319a20.99 20.99 0 0 1 2.049 1.254c.357.248.316.402.021.674-1.879 1.731-3.519 3.666-4.844 5.854-1.891 3.122-3.084 6.467-3.144 10.156-.018 1.092.013 1.104 1.113 1.036 1.095-.068 2.141-.372 3.192-.649.229-.06.422-.13.619.093.959 1.085 2.225 1.721 3.491 2.354l.304.148h.001zm4.427-26.038A33.89 33.89 0 0 1 24.889.293c1.54-.178 3.084-.235 4.633-.121.86.063.858.043.863.879.022 4.474-.618 8.828-2.385 12.97-1.452 3.404-3.479 6.399-6.341 8.797-.434.364-.819.459-1.338.218-.803-.373-1.524-.865-2.245-1.371-.435-.305-.347-.492.001-.8 2.441-2.161 4.471-4.656 5.972-7.556 1.394-2.695 2.25-5.54 2.29-8.604.007-.548-.203-.696-.702-.69-1.255.014-2.456.326-3.662.614-.183.043-.349.148-.515-.057-.978-1.206-2.351-1.858-3.765-2.64h0z"></path></g><path d="M13.001 15.145v-.057c0-.888.002-.928.907-.874.583.034.748-.136.715-.717-.053-.928-.014-.93.902-.93 1.024 0 1.05.001 1.021 1.042-.014.487.145.614.615.601 1.046-.027 1.046-.004 1.046 1.037 0 .916-.002.958-.946.902-.606-.036-.743.173-.715.736.045.91.011.911-.922.911-1.014 0-1.039-.001-1.005-1.003.016-.478-.073-.67-.612-.644-1.005.049-1.006.012-1.006-1.004h.001z" fill="#f26522"></path></g><defs><clipPath id="LogoIconA"><path d="M0 0h81.027v30H0z" fill="#fff" transform="translate(.5)"></path></clipPath></defs></svg>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
  <div style="margin: 0px auto; max-width: 600px;">
    <table cellpadding="0" cellspacing="0" role="presentation" style="width: 100%; text-align: center; border:0 ;">
      <tbody>
      <tr>
        <td style="font-size: 0px; padding: 20px 0; text-align: center;">
          <div class="mj-column-per-100 mj-outlook-group-fix" style="display: inline-block; vertical-align: top; width: 100%;">
            <table  cellpadding="0" cellspacing="0" role="presentation" style="width: 100%;">
              <thead>
              <tr>
                <th class="topText" style="font-size: 0px; padding: 10px 10px 25px 10px; word-break: break-word;">
                  <div style="font-family: Ubuntu, Helvetica, Arial, sans-serif; font-size: 25px; line-height: 1; text-align: left; color: #000000;">
                    mDoc Prescription ${title ?? ""}
                  </div>
                </th>
              </tr>
              </thead>
            </table>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
  <div style="margin: 0px auto; max-width: 600px;">
    <table id="medications-table" align="center"  cellpadding="0" cellspacing="0" role="presentation" style="width: 100%; border-collapse: collapse;">
      <thead style="font-family: Ubuntu, Helvetica, Arial, sans-serif; font-size: 14px; font-weight: 500; text-align: left; color: #000000; vertical-align: top;">
        <tr>
          <th style="height: 50px; width: 20%;">Medication Name</th>
          <th style="width: 15%;">Start Date</th>
          <th style="width: 15%;">Duration in days</th>
          <th style="width: 15%;">Prescribed By</th>
          <th style="">Frequency</th>
          <th style="width: 10%;">Dosage Unit</th>
        </tr>
        <tr style="border-bottom: 1px solid black;">
          <th style="padding-bottom: 10px;"></th>
        </tr>
      </thead>
      <tbody style="font-size: 12px;">
        ${tableBody}
        <tr style="border-bottom: 1px solid black;">
          <td style="padding-bottom: 10px;"></td>
        </tr>
      </tbody>
    </table>
  </div>
  <div style="margin: 0px auto; max-width: 600px;">
    <table align="center"  cellpadding="0" cellspacing="0" role="presentation" style="width: 100%">
      <tbody>
      <tr>
        <td style="font-size: 0px; padding: 20px 0; text-align: center;">
          <div class="mj-column-per-100 mj-outlook-group-fix" style="display: inline-block; vertical-align: top; width: 100%;">
            <table  cellpadding="0" cellspacing="0" role="presentation" style="width: 100%;">
              <tbody>
              <tr>
                <td align="left" style="font-size: 0px; padding: 10px 25px; word-break: break-word;">
                  <div style="font-family: Ubuntu, Helvetica, Arial, sans-serif; font-size: 15px; line-height: 0px; text-align: left; color: #000000;">Name of Referring coach: ${
                    coach?.name ?? ""
                  }
                  </div>
                </td>
              </tr>
              <tr>
                <td align="left" style="font-size: 0px; padding: 10px 25px; word-break: break-word;">
                  <div style="font-family: Ubuntu, Helvetica, Arial, sans-serif; font-size: 15px; line-height: 0px; text-align: left; color: #000000;">Email: ${
                    coach?.email ?? ""
                  }
                  </div>
                </td>
              </tr>
              <tr>
                <td align="left" style="font-size: 0px; padding: 10px 25px; word-break: break-word;">
                  <div style="font-family: Ubuntu, Helvetica, Arial, sans-serif; font-size: 15px; line-height: 0px; text-align: left; color: #000000;">Phone number: ${
                    coach?.phone ?? ""
                  }
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  align="left"
                  style="font-size: 0px; padding: 10px 25px; word-break: break-word"
                >
                  <div
                    style="
                                          font-family: Ubuntu, Helvetica, Arial, sans-serif;
                                          font-size: 15px;
                                          line-height: 0px;
                                          text-align: left;
                                          color: #000000;
                                          "
                  >
                    Organization: mDoc Healthcare
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
  <div style="margin: 0px auto; max-width: 600px">
    <table
      align="center"
      cellpadding="0"
      cellspacing="0"
      role="presentation"
      style="width: 100%"
    >
      <tbody>
      <tr>
        <td style="direction: ltr; font-size: 0px; padding: 20px 0; text-align: center">
          <div
            class="mj-column-per-100 mj-outlook-group-fix"
            style="
                           font-size: 0px;
                           text-align: left;
                           direction: ltr;
                           display: inline-block;
                           vertical-align: top;
                           width: 100%;
                           "
          >
            <table
              cellpadding="0"
              cellspacing="0"
              role="presentation"
              style="vertical-align: top"
              width="100%"
            >
              <tbody>
              <tr>
                <td
                  align="left"
                  style="font-size: 0px; padding: 10px 25px; word-break: break-word"
                >
                  <div
                    style="
                                          font-family: Ubuntu, Helvetica, Arial, sans-serif;
                                          font-size: 15px;
                                          line-height: 0px;
                                          text-align: left;
                                          color: #000000;
                                          "
                  >
                    Patient contact details:
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  align="left"
                  style="font-size: 0px; padding: 10px 25px; word-break: break-word"
                >
                  <div
                    style="
                      font-family: Ubuntu, Helvetica, Arial, sans-serif;
                      font-size: 15px;
                      line-height: 0px;
                      text-align: left;
                      color: #000000;
                      "
                  >
                    Name: ${member?.name ?? "n/a"}
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  align="left"
                  style="font-size: 0px; padding: 10px 25px; word-break: break-word"
                >
                  <div
                    style="
                      font-family: Ubuntu, Helvetica, Arial, sans-serif;
                      font-size: 15px;
                      line-height: 0px;
                      text-align: left;
                      color: #000000;
                      "
                  >
                    Phone: ${member?.phone ?? "n/a"}
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  align="left"
                  style="font-size: 0px; padding: 10px 25px; word-break: break-word"
                >
                  <div
                    style="
                                          font-family: Ubuntu, Helvetica, Arial, sans-serif;
                                          font-size: 15px;
                                          line-height: 0px;
                                          text-align: left;
                                          color: #000000;
                                          "
                  >
                    Address: ${member?.email ?? member?.address}
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
  <div style="margin: 0px auto; max-width: 600px">
    <table
      align="center"
      cellpadding="0"
      cellspacing="0"
      role="presentation"
      style="width: 100%"
    >
      <tbody>
      <tr>
        <td style="direction: ltr; font-size: 0px; padding: 20px 0; text-align: center">
          <div
            class="mj-column-per-100 mj-outlook-group-fix"
            style="
                           font-size: 0px;
                           text-align: left;
                           direction: ltr;
                           display: inline-block;
                           vertical-align: top;
                           width: 100%;
                           "
          >
            <table
              cellpadding="0"
              cellspacing="0"
              role="presentation"
              style="vertical-align: top"
              width="100%"
            >
              <tbody>
              <tr>
                <td
                  align="left"
                  style="font-size: 0px; padding: 10px 25px; word-break: break-word"
                >
                  <div
                    style="
                                          font-family: Ubuntu, Helvetica, Arial, sans-serif;
                                          font-size: 15px;
                                          line-height: 0px;
                                          text-align: left;
                                          color: #000000;
                                          "
                  >
                    Name of referral center: ${member?.facilityName ?? "n/a"}
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  align="left"
                  style="font-size: 0px; padding: 10px 25px; word-break: break-word"
                >
                  <div
                    style="
                                          font-family: Ubuntu, Helvetica, Arial, sans-serif;
                                          font-size: 15px;
                                          line-height: 0px;
                                          text-align: left;
                                          color: #000000;
                                          "
                  >
                    Location: ${member.facilityLocation ?? "n/a"}
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
  <div class="footer" style="margin: 0px auto; max-width: 100%; margin-top: 10vh">
    <table
      align="center"
      cellpadding="0"
      cellspacing="0"
      role="presentation"
      style="width: 100%"
    >
      <tbody>
      <tr>
        <td
          style="border-top: 0.6px solid #47b462;
            direction: ltr;
            font-size: 0px;
            padding: 20px 0;
            padding-top: 0px; text-align: center;"
        >
          <div
            class="mj-column-per-33-333333333333336 mj-outlook-group-fix"
            style="
             font-size: 0px;
             text-align: left;
             direction: ltr;
             display: inline-block;
             vertical-align: top;
             width: 100%;
             "
          >
            <table
              cellpadding="0"
              cellspacing="0"
              role="presentation"
              style="vertical-align: top"
              width="100%"
            >
              <tbody>
              <tr>
                <td
                  align="center"
                  vertical-align="middle"
                  style="font-size: 0px; padding: 10px 25px; word-break: break-word"
                >
                  <table
                    cellpadding="0"
                    cellspacing="0"
                    role="presentation"
                    style="border-collapse: separate; line-height: 100%"
                  >
                    <tr>
                      <td
                        align="center"
                        bgcolor="transparent"
                        role="presentation"
                        style="
                                                border: none;
                                                border-radius: 3px;
                                                cursor: auto;
                                                mso-padding-alt: 10px 25px;
                                                background: transparent;
                                                "
                        valign="middle"
                      >
                        <a
                          href="https://www.mymdoc.com/"
                          style="
                                                   display: inline-block;
                                                   background: transparent;
                                                   color: #47b462;
                                                   font-family: Ubuntu, Helvetica, Arial, sans-serif;
                                                   font-size: 15px;
                                                   font-weight: normal;
                                                   line-height: 120%;
                                                   letter-spacing: 0.5px;
                                                   margin: 0;
                                                   text-decoration: none;
                                                   text-transform: none;
                                                   padding: 10px 25px;
                                                   mso-padding-alt: 0px;
                                                   border-radius: 3px;
                                                   "
                          target="_blank"
                        >
                          mymdoc.com
                        </a>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <div
            class="mj-column-per-33-333333333333336 mj-outlook-group-fix"
            style="
                           font-size: 0px;
                           text-align: left;
                           direction: ltr;
                           display: inline-block;
                           vertical-align: top;
                           width: 100%;
                           "
          >
            <table
              cellpadding="0"
              cellspacing="0"
              role="presentation"
              style="vertical-align: top"
              width="100%"
            >
              <tbody>
              <tr>
                <td
                  style="font-size: 0px; padding: 10px 25px; word-break: break-word; text-align: center"
                >
                  <div
                    style="
                                          font-family: Ubuntu, Helvetica, Arial, sans-serif;
                                          font-size: 13px;
                                          line-height: 20px;
                                          text-align: center;
                                          color: #000000;
                                          "
                  >
                    1A Hakeem Dickson Drive Off T.F. Kuboye Street Lekki Phase One, Lagos.
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <div
            class="mj-column-per-33-333333333333336 mj-outlook-group-fix"
            style="
                           font-size: 0px;
                           text-align: left;
                           direction: ltr;
                           display: inline-block;
                           vertical-align: top;
                           width: 100%;
                           "
          >
            <table
              cellpadding="0"
              cellspacing="0"
              role="presentation"
              style="vertical-align: top"
              width="100%"
            >
              <tbody>
              <tr>
                <td
                  style="font-size: 0px; padding: 10px 25px; word-break: break-word"
                >
                  <div
                    style="
                                          font-family: Ubuntu, Helvetica, Arial, sans-serif;
                                          font-size: 13px;
                                          line-height: 1;
                                          text-align: center;
                                          color: #000000;
                                          "
                  >
                    +2348178348579
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</div>
</div>
</body>
</html>
  `;
  return template;
};
